import * as _ from 'underscore';

import { SignInUrlBuilder } from '@tableau/shared-urls';

import { UrlParamKeys } from '../../app/UrlParamKeys';
import { Helpers } from '../helpers/Helpers';
import { Module } from '../Module';
import { ServerService } from './ServerService';
import { SiteUrlNameCapturingRegex } from './SiteUrlMatcherFactory';
import { WindowLocationService } from './WindowLocationService';

export interface IExternalLoginEntryPoint {
  path: string;
  queryParams: _.Dictionary<any>;
}

export class ExternalAuthService {

  private static SamlLoginUrl: string = 'vizportal/api/saml';
  private static OpenIdLoginUrl: string = 'vizportal/api/web/v1/auth/openId';
  private loginParams: SignInUrlBuilder.QueryParams = {};

  public static $inject = [
    '$location',
    'ServerService',
    'WindowLocationService'
  ];

  constructor(private $location: ng.ILocationService,
              private serverService: ServerService,
              private windowLocationService: WindowLocationService) {
  }

  public getLoginUrl(loginParams: SignInUrlBuilder.QueryParams): string {
    this.loginParams = loginParams;

    const entryPoint = this.getExternalLoginEntryPoint(true);
    const url = this.getEntryPointUrl(entryPoint);
    return url;
  }

  public login(loginParams: SignInUrlBuilder.QueryParams, entryPoint?:  IExternalLoginEntryPoint) {
    this.loginParams = loginParams;

    entryPoint = this.getExternalLoginEntryPoint(false, entryPoint);
    const url = this.getEntryPointUrl(entryPoint);
    this.windowLocationService.navigateToWithoutAddingToHistory(url);
  }

  private getExternalLoginEntryPoint(frontDoor: boolean, entryPoint?:  IExternalLoginEntryPoint): IExternalLoginEntryPoint {
    if (this.serverService.isSiteSamlEnabled() && this.serverService.isServerDomainFamilyLocal()) {
      return {
        path: ExternalAuthService.SamlLoginUrl,
        queryParams: frontDoor ? {} : this.getSiteSamlLoginQueryParams(entryPoint)
      };
    } else if (this.serverService.isAuthenticationSaml()) {
      return {
        path: ExternalAuthService.SamlLoginUrl,
        queryParams: frontDoor ? {} : this.getSamlLoginQueryParams()
      };
    } else if (this.serverService.isAuthenticationOpenId()) {
      return {
        path: ExternalAuthService.OpenIdLoginUrl,
        queryParams: frontDoor ? {} : this.getOpenIdLoginQueryParams()
      };
    } else {
      throw new Error("This server doesn't use a supported authentication type");
    }
  }

  // params are a subset of those supported by SAMLExtendedEntryPoint.java and SAMLExtendedProcessingFilter.java (via RelayState)
  private getSamlLoginQueryParams(): _.Dictionary<any> {
    // The 'sendPodInfo' parameter ensures that the server doesn't try to redirect the close-pop-up page to
    // the correct POD for the site. Instead, it redirects to a different page on the destination
    // POD which can send the POD info back to the parent window.

    const queryParams: _.Dictionary<any> = {
      dest:        this.getRedirect(),
      target_site: this.getSiteUrlName(),
      sendPodInfo: this.getBooleanQueryParam(UrlParamKeys.ClosePopupWhenDone),
      authSetting: this.getStringQueryParam(UrlParamKeys.AuthSetting),
      siteLuid:    this.getStringQueryParam(UrlParamKeys.SiteLuid),
      embedded:    this.getBooleanQueryParam(UrlParamKeys.Embedded)
    };
    if (queryParams['target_site'] == null) {
      // empty target_site has special meaning for the SAML entry point: it refers to the Default site
      delete queryParams['target_site'];
    }
    return queryParams;
  }

  private getSiteSamlLoginQueryParams(entryPoint?:  IExternalLoginEntryPoint): _.Dictionary<any> {
    const queryParams: _.Dictionary<any> = entryPoint ? entryPoint.queryParams : {};
    if (!queryParams['target_site']) {
      // empty target_site has special meaning for the SAML entry point: it refers to the Default site
      delete queryParams['target_site'];
    }
    queryParams['dest'] = this.getRedirect();
    return queryParams;
  }

  // params are a subset of those supported by openIdInitLoginHandler() in WebClientGetAuthenticationController.java
  private getOpenIdLoginQueryParams(): _.Dictionary<string> {
    return {
      path: this.getRedirect(),
      siteUrlName: this.getSiteUrlName()
    };
  }

  private getRedirect(): string {

    if (this.loginParams.externalRedirect) {
      return this.loginParams.externalRedirect;
    } else if (this.loginParams.redirect) {
      return this.windowLocationService.getPathQuery() + '#' + this.loginParams.redirect;
    } else if (this.getBooleanQueryParam(UrlParamKeys.ClosePopupWhenDone)) {
      return '/' + this.serverService.serverLanguage() + '/closePopup.html';
    } else {
     return '/';
    }
  }

  private getSiteUrlName(): string {
    if (_.isString(this.loginParams.site)) {
      return this.loginParams.site;
    } else if (_.isString(this.loginParams.redirect)) {
      const match = this.loginParams.redirect.match(SiteUrlNameCapturingRegex);
      return match && match[1];
    } else {
      return null;
    }
  }

  private getEntryPointUrl(entryPoint: IExternalLoginEntryPoint): string {
    let url = entryPoint.path;
    const paramJoinSymbol = (url.indexOf('?') >= 0) ? '&' : '?';
    const queryParams = Helpers.serialize(entryPoint.queryParams);
    if (queryParams.length > 0) {
      url += paramJoinSymbol + queryParams;
    }
    return url;
  }

  private getBooleanQueryParam(key: string): boolean {
    return Helpers.BooleanQueryParamRegex.test(this.getStringQueryParam(key));
  }

  private getStringQueryParam(key: string): string {
    return this.$location.search()[key];
  }
}

Module.instance().service('ExternalAuthService', ExternalAuthService);
